import React, { useState, useEffect } from "react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

export default function Number() {
  const [numbers, setNumbers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNumber, setNewNumber] = useState({
    didNumber: "",
    duration: "",
    country: "",
    expiryDate: ""
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = "https://didrouteapi.com/api/numbers";

  // Fetch numbers on component mount
  useEffect(() => {
    const loadNumbers = async () => {
      setIsLoading(true);
      await fetchNumbers();
      setIsLoading(false);
    };
    loadNumbers();
  }, []);

  // Fetch numbers from API
  const fetchNumbers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      // console.log('API Response:', result); // Debug log
      
      // Handle nested data structure
      if (result.success && result.data && result.data.data && Array.isArray(result.data.data)) {
        setNumbers(result.data.data);
      } else {
        console.error('Invalid data format received:', result);
        setNumbers([]);
      }
    } catch (error) {
      console.error('Error fetching numbers:', error);
      setNumbers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        fetchNumbers(); // Refresh the list
        setSuccessMessage('Status updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  // Handle edit
  const handleEdit = (number) => {
    setEditingId(number.id);
    setEditForm({
      didNumber: number.did_number,
      duration: number.call_rate,
      country: number.country,
      expiryDate: number.expiry_date
    });
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          didNumber: editForm.didNumber,
          duration: editForm.duration,
          country: editForm.country,
          expiryDate: editForm.expiryDate
        })
      });
      const result = await response.json();
      if (result.success) {
        await fetchNumbers();
        setEditingId(null);
        setSuccessMessage('Number updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating number:', error);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this number?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          fetchNumbers();
          setSuccessMessage('Number deleted successfully');
          setTimeout(() => setSuccessMessage(''), 3000);
        }
      } catch (error) {
        console.error('Error deleting number:', error);
      }
    }
  };

  // Handle create
  const handleCreateNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          didNumber: newNumber.didNumber,
          duration: newNumber.duration,
          country: newNumber.country,
          expiryDate: newNumber.expiryDate
        })
      });
      const result = await response.json();
      if (result.success) {
        await fetchNumbers(); // Refresh the list
        setShowCreateModal(false);
        setNewNumber({
          didNumber: "",
          duration: "",
          country: "",
          expiryDate: ""
        });
        setSuccessMessage('Number created successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error creating number:', error);
    }
  };

  // Handle search
  const handleSearch = async (e) => {
    setSearchTerm(e.target.value);
    try {
      const token = localStorage.getItem('token');
      // If search term is empty, fetch all numbers
      const url = e.target.value ? `${API_URL}/search?query=${e.target.value}` : API_URL;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      
      // Handle nested data structure
      if (result.success && result.data && result.data.data && Array.isArray(result.data.data)) {
        setNumbers(result.data.data);
      } else {
        setNumbers([]);
      }
    } catch (error) {
      console.error('Error searching numbers:', error);
      setNumbers([]);
    }
  };

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      {successMessage && (
        <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center sm:justify-between">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Manage Number
                </h1>
              </div>

              <div className="mt-4 sm:mt-0 sm:ml-4 flex gap-4">
                <input
                  type="text"
                  placeholder="Search DID..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="px-3 py-2  text-gray-900 text-sm border border-gray-700 placeholder:text-gray-900 rounded-lg focus:outline-none focus:ring focus:ring-indigo-500"
                />

                {/* <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-indigo-600 px-4 py-2 text-white rounded-lg hover:bg-indigo-500"
                >
                  Add Number
                </button> */}
              </div>
            </div>

            {/* Table */}
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0"
                        >
                          DID
                        </th>

                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Call Rate
                        </th>

                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Country
                        </th>

                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Expiry Date
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    {!isLoading && (
                      <tbody className="divide-y divide-gray-800">
                        {numbers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-gray-900">
                              No numbers found
                            </td>
                          </tr>
                        ) : (
                          numbers.map((number) => (
                            <tr key={number.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                                {number.did_number}
                              </td>

                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                {number.call_rate}
                              </td>

                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                {number.country}
                              </td>

                              <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                                {new Date(
                                  number.expiry_date
                                ).toLocaleDateString()}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-xs font-medium sm:pr-0 flex items-center gap-4">
                                {/* <button
                                  onClick={() => handleEdit(number)}
                                  className="text-indigo-400 hover:text-indigo-300"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button> */}
                                {/* <button
                                  onClick={() => handleDelete(number.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button> */}
                                <button
                                  onClick={() => handleStatusToggle(number.id)}
                                  className={`px-3 py-1 rounded-full text-xs ${
                                    number.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {number.status}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    )}
                  </table>
                  {isLoading && (
                    <div className="text-center py-4 text-gray-900">
                      Loading...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingId && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-gray-900 p-6 rounded-lg shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Edit Number
            </h2>
            <input
              type="text"
              placeholder="DID Number"
              value={editForm.didNumber}
              onChange={(e) =>
                setEditForm({ ...editForm, didNumber: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="text"
              placeholder="Call Rate"
              value={editForm.duration}
              onChange={(e) =>
                setEditForm({ ...editForm, duration: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="text"
              placeholder="Country"
              value={editForm.country}
              onChange={(e) =>
                setEditForm({ ...editForm, country: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="date"
              value={editForm.expiryDate}
              onChange={(e) =>
                setEditForm({ ...editForm, expiryDate: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditingId(null)}
                className="px-3 py-2 bg-gray-600 text-white rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-3 py-2 bg-indigo-600 text-white rounded-md"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-gray-900 p-6 rounded-lg shadow-xl">
            <h2 className="text-lg font-semibold text-white mb-4">
              Add New Number
            </h2>
            <input
              type="text"
              placeholder="DID Number"
              value={newNumber.didNumber}
              onChange={(e) =>
                setNewNumber({ ...newNumber, didNumber: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="text"
              placeholder="Call Rate"
              value={newNumber.duration}
              onChange={(e) =>
                setNewNumber({ ...newNumber, duration: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="text"
              placeholder="Country"
              value={newNumber.country}
              onChange={(e) =>
                setNewNumber({ ...newNumber, country: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <input
              type="date"
              value={newNumber.expiryDate}
              onChange={(e) =>
                setNewNumber({ ...newNumber, expiryDate: e.target.value })
              }
              className="block w-full mb-3 rounded-md border-0 bg-gray-700 text-white px-3 py-1.5"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-3 py-2 bg-gray-600 text-white rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNumber}
                className="px-3 py-2 bg-indigo-600 text-white rounded-md"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
